import { useEffect, useState } from 'react';
import client from './api';

/**
 * Menülerde gösterilmeyecek oyun kategorileri.
 *
 * İki ayrı neden vardır ve ikisi de gizlemeyi gerektirir:
 *   * `disabled` — yönetici kategoriyi panelden kapatmıştır.
 *   * `game_count === 0` — kategori açıktır ama kataloğunda hiç oyun yoktur.
 *     Bu genellikle o kategoriyi besleyen sağlayıcının bağlı olmamasından
 *     gelir (ör. crash ve canlı casino oyunları Drakon/ScorpioPlay'den gelir;
 *     yalnızca Gold Palace bağlıyken bu kategoriler boş kalır). Menüde
 *     bırakılırsa oyuncu "oyun bulunamadı" diyen boş bir sayfaya düşer.
 */
export const hiddenCategoriesFrom = (data) => {
  const disabled = data?.disabled || [];
  const empty = (data?.items || [])
    .filter((item) => item.enabled !== false && Number(item.game_count) === 0)
    .map((item) => item.id);
  return [...new Set([...disabled, ...empty])];
};

// Son bilinen liste localStorage'da durur. Yanıt gelene kadar boş listeyle
// çizmek "hiçbir şey kapalı değil" demektir: kapalı kategorinin kartı ve menü
// maddesi her açılışta bir an görünüp düşüyor, altındaki kartlar kayıyordu.
export const HIDDEN_CATEGORIES_CACHE_KEY = 'app:hidden-categories-v1';

const storageOf = (storage) => (
  storage !== undefined
    ? storage
    : (typeof window !== 'undefined' ? window.localStorage : null)
);

export const readCachedHiddenCategories = (storage) => {
  try {
    const raw = storageOf(storage)?.getItem(HIDDEN_CATEGORIES_CACHE_KEY);
    const list = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(list)) return null;
    return list.filter((item) => typeof item === 'string');
  } catch {
    return null;
  }
};

export const cacheHiddenCategories = (list, storage) => {
  try {
    storageOf(storage)?.setItem(HIDDEN_CATEGORIES_CACHE_KEY, JSON.stringify(list));
  } catch {
    // Depolama kapalıysa bir sonraki açılış yine yanıtı bekler.
  }
};

// Aynı sayfada kabuk ve vitrin aynı listeyi ister; eşzamanlı istekler tek
// çağrıya bağlanır, sayfa içinde bilinen son değer de bellekte tutulur.
let memoryHidden = null;
let inflight = null;

const fetchHidden = () => {
  if (!inflight) {
    inflight = client.get('/game-categories')
      .then(({ data }) => {
        const list = hiddenCategoriesFrom(data);
        memoryHidden = list;
        cacheHiddenCategories(list);
        return list;
      })
      .finally(() => { inflight = null; });
  }
  return inflight;
};

export const resetHiddenCategoriesMemory = () => {
  memoryHidden = null;
  inflight = null;
};

/**
 * `known`, listenin gerçekten bilinip bilinmediğini söyler (bellek, önbellek
 * ya da yanıt). Bilinmeden çizilen kart kapalı olabilir; vitrin bu yüzden
 * bilinene kadar kategoriye bağlı kart çizmez.
 */
export const useHiddenCategoriesState = () => {
  const [hidden, setHidden] = useState(() => memoryHidden || readCachedHiddenCategories());

  useEffect(() => {
    let active = true;
    fetchHidden()
      .then((list) => { if (active) setHidden(list); })
      // Uç yanıt vermezse eski davranış: hiçbir kategori gizlenmez.
      .catch(() => { if (active) setHidden((current) => current || []); });
    return () => { active = false; };
  }, []);

  return { hidden: hidden || [], known: hidden !== null };
};

export const useHiddenCategories = () => useHiddenCategoriesState().hidden;

export default useHiddenCategories;
